import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import AppLayout from '~/components/appLayout'
import SearchIcon from '~/components/icons/ui/16px_zoom-2.svg'
import {
  legalEntitySearchOnlyQueryFactory,
  formatLegalEntitySearchItem,
} from '../legalEntityGeoprocessing/map/useLegalEntitySearchWithGeoQueryFactory'
import { colors } from '~/design'
import LegalEntityReadOne from '../legalEntity/read/legalEntityReadOne.component'
import Logo from '~/meta/logo.svg'
import { entity, equals, query, string } from '~/prix'
import useItem from '~/prix/react/hooks/item'
import useItems from '~/prix/react/hooks/items'
import { maskNumbers, masks } from '~/prix/utils/types/mask'
import { useParams, useNavigate } from 'react-router-dom'
import { trackAnalyticsEvent } from '~/utils/client/analytics'

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  height: 100%;
  min-height: 0;

  @media (max-width: 1200px) {
    padding: 16px;
  }

  @media (max-width: 700px) {
    padding: 14px;
  }
`

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid hsl(0, 0%, 80%);
  border-radius: 8px;
  box-sizing: border-box;
  overflow: hidden;
  min-height: 42px;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: hsl(0, 0%, 70%);
  }

  &:focus-within {
    border-color: #2684ff;
    box-shadow: none;
  }

  input {
    width: 100%;
    border: 0;
    outline: 0;
    height: 40px;
    font-size: 16px;
    font-weight: 400;
    color: #2e3949;
    padding: 0 10px;
  }

  input::placeholder {
    color: #5d6b80;
  }

  .input-action {
    width: 32px;
    height: 32px;
    border: 0;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .clear-action {
    color: #7e8a9b;
    font-size: 24px;
    line-height: 1;
    margin-right: 2px;
  }

  .search-action {
    border-left: 1px solid #d9e0eb;
    padding-left: 8px;
    margin-right: 6px;

    svg {
      display: block;
    }
  }
`

const SearchMeta = styled.div`
  color: #2e3949;
  font-size: 13px;
  line-height: 1.3;
  margin: 0 auto;
`

const ResultsList = styled.div<{ $fillHeight: boolean }>`
  width: 100%;
  ${({ $fillHeight }) => ($fillHeight ? 'flex: 1;' : 'height: fit-content;')}
  min-height: 0;
  max-height: ${({ $fillHeight }) => ($fillHeight ? 'none' : 'calc(100vh - 220px)')};
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d4dce8;
  border-radius: 10px;
  box-shadow: 0px 12px 20px rgba(20, 46, 82, 0.12);

  @media (max-width: 1200px) {
    flex: none;
    height: fit-content;
    max-height: 185px;
  }
`

const ResultItem = styled.button<{ $isActive: boolean }>`
  width: 100%;
  border: 0;
  border-bottom: 1px solid #edf1f5;
  background: ${({ $isActive }) => ($isActive ? '#abd6ff' : '#fff')};
  padding: 12px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ $isActive }) => ($isActive ? '#abd6ff' : '#f0f7ff')};
  }

  &:last-child {
    border-bottom: 0;
  }
`

const TitleWrapper = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 1.2;
  color: #2e3949;
`

const SubtitleWrapper = styled.div`
  margin-top: 3px;
  color: #2e3949;
  font-size: 12px;
`

const ListFooter = styled.div`
  color: #69778a;
  font-size: 12px;
  text-align: center;
  padding: 10px;
  border-top: 1px solid #edf1f5;
`

const PageContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;

  @media (max-width: 1200px) {
    flex-direction: column;
    height: auto;
    min-height: 100%;
  }
`

const LeftSide = styled.div`
  width: 27%;
  min-width: 300px;
  box-sizing: border-box;
  background: #f5f7fb;

  @media (max-width: 1200px) {
    width: 100%;
    min-width: 100%;
    order: 1;
  }
`

const Divider = styled.div`
  width: 1px;
  background: #ddd;

  @media (max-width: 1200px) {
    display: none;
  }
`

const RightSide = styled.div`
  flex: 1;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  overflow: auto;

  @media (max-width: 1200px) {
    z-index: 1;
    width: 100%;
    order: 3;
    overflow: visible;
  }
`

const CompanySelected = styled.div`
  margin: 14px 14px 0;
  padding: 14px 16px;
  border-radius: 12px 12px 0 0;
  background: linear-gradient(135deg, #0f438a 0%, #1b5ab0 100%);
  color: #fff;
  box-shadow: 0px 12px 24px rgba(20, 46, 82, 0.22);

  @media (max-width: 700px) {
    box-shadow: none;
    margin: 14px 14px 0;
  }
`

const CompanySelectedTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
`

const CompanySelectedMeta = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const CompanySelectedTag = styled.span`
  font-size: 12px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 999px;
  padding: 4px 10px;
`

const LegalEntityReadOneScope = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
  width: 100%;

  article {
    margin: 14px 14px 14px 20px !important;
    column-gap: 2rem !important;
    row-gap: 2.2rem !important;
    justify-items: stretch !important;
    padding-bottom: 56px !important;
  }

  article > * {
    width: 100%;
  }

  article .map {
    margin-top: 0 !important;
  }

  @media (max-width: 1290px) {
    article {
      margin: 0 14px !important;
      padding: 0 14px 48px !important;
      row-gap: 1.8rem !important;
      column-gap: 1.4rem !important;
    }
  }

  @media (max-width: 1200px) {
    min-height: 50vh;

    article {
      margin: 0 14px !important;
      padding: 0 10px 48px !important;
    }

    article .map {
      padding: 0 !important;
    }

    article > div.map + div,
    article > div:first-child:not(.map) {
      margin: 0 !important;
      padding: 12px 0 !important;
    }
  }

  @media (max-width: 700px) {
    article {
      margin: 0 14px !important;
      padding: 14px !important;
      gap: 20px !important;
      border-radius: 0 0 12px 12px !important;
      box-shadow: 0px 0px 30px rgba(20, 46, 82, 0.2);
    }
  }
`

const SelectedCompanyPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    min-height: 50vh;
  }

  @media (max-width: 700px) {
    padding-bottom: 20px;
  }
`

const CenteredLogo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;

  @media (max-width: 1200px) {
    min-height: 50vh;
  }
`

export default function SearchLegalEntity() {
  const navigate = useNavigate()
  const { searchLegalEntityId } = useParams()
  const [selectedLegalEntityId, setSelectedLegalEntityId] = useState<string | null>(
    searchLegalEntityId ?? null,
  )
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearchInput, setDebouncedSearchInput] = useState('')
  const [visibleCount, setVisibleCount] = useState(30)
  const previousDebouncedSearchInputRef = useRef(debouncedSearchInput)

  useEffect(() => {
    setSelectedLegalEntityId(searchLegalEntityId ?? null)
  }, [searchLegalEntityId])

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchInput(searchInput.trim())
    }, 550)

    return () => {
      clearTimeout(debounceTimer)
    }
  }, [searchInput])

  useEffect(() => {
    previousDebouncedSearchInputRef.current = debouncedSearchInput
  }, [debouncedSearchInput])

  const queryFactory = useMemo(
    () =>
      legalEntitySearchOnlyQueryFactory({
        hasUserRoles: true,
        userGeoState: { items: [] },
      }),
    [],
  )

  const selectedLegalEntityHeader = useItem(
    () =>
      query('legalEntity')
        .select({
          id: entity('legalEntity').property('id'),
          corporateName: entity('legalEntity').property('corporateName'),
          tradeName: entity('legalEntity').property('tradeName'),
          cnpj: entity('legalEntity').property('cnpj'),
          city: entity('legalEntity').property('city'),
          state: entity('legalEntity').property('state'),
        })
        .where(
          equals(
            entity('legalEntity').property('id'),
            string().value((selectedLegalEntityId ?? '') as string),
          ),
        )
        .limit(1),
    [selectedLegalEntityId],
    { autoLoad: Boolean(selectedLegalEntityId) },
  )

  const canSearch = debouncedSearchInput.length >= 3

  const searchedLegalEntities = useItems(
    () => queryFactory(debouncedSearchInput) as any,
    [queryFactory, debouncedSearchInput],
    { autoLoad: canSearch },
  )

  const rankedLegalEntityResults = useMemo(() => {
    if (!canSearch) {
      return []
    }

    return (searchedLegalEntities.items ?? [])
      .sort((a: any, b: any) => (a.searchRank as number) - (b.searchRank as number))
      .reverse()
      .slice(0, 300)
  }, [canSearch, searchedLegalEntities.items])

  useEffect(() => {
    setVisibleCount(30)
  }, [debouncedSearchInput])

  const visibleResults = useMemo(
    () =>
      rankedLegalEntityResults
        .slice(0, visibleCount)
        .map((item: any) => formatLegalEntitySearchItem(item)),
    [rankedLegalEntityResults, visibleCount],
  )

  const hasMoreItems = visibleCount < rankedLegalEntityResults.length

  const handleSelectLegalEntity = useCallback(
    (item: any) => {
      const selectedId = String(item.id)
      trackAnalyticsEvent('feature_used', {
        feature: 'legal_entity_selected',
        source_screen: 'legal_entity_search',
      })
      navigate(`/app/searchLegalEntity/${selectedId}`, { replace: false })
      setSelectedLegalEntityId(selectedId)
    },
    [navigate],
  )

  const handleSearchAction = useCallback(() => {
    trackAnalyticsEvent('feature_used', {
      feature: 'legal_entity_search',
      result_count: rankedLegalEntityResults.length,
      source_screen: 'legal_entity_search',
    })
    if (visibleResults[0]) {
      handleSelectLegalEntity(visibleResults[0])
    }
  }, [handleSelectLegalEntity, visibleResults, rankedLegalEntityResults.length])

  const handleClearSearch = useCallback(() => {
    setSearchInput('')
    setDebouncedSearchInput('')
    setVisibleCount(30)
    setSelectedLegalEntityId(null)
    navigate('/app/searchLegalEntity', { replace: true })
  }, [navigate])

  const handleResultListScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasMoreItems) {
        return
      }

      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
      const isNearBottom = scrollHeight - (scrollTop + clientHeight) <= 120

      if (isNearBottom) {
        setVisibleCount(currentVisibleCount =>
          Math.min(currentVisibleCount + 30, rankedLegalEntityResults.length),
        )
      }
    },
    [hasMoreItems, rankedLegalEntityResults.length],
  )

  const hasResults = rankedLegalEntityResults.length > 0
  const trimmedSearchInput = searchInput.trim()
  const canSearchFromInput = trimmedSearchInput.length >= 3
  const isWaitingDebounce = canSearchFromInput && trimmedSearchInput !== debouncedSearchInput
  const hasDebouncedSearchChanged = previousDebouncedSearchInputRef.current !== debouncedSearchInput
  const isSearchingCompanies =
    canSearchFromInput &&
    (isWaitingDebounce || hasDebouncedSearchChanged || searchedLegalEntities.isLoading)
  const shouldShowResults =
    hasResults &&
    canSearchFromInput &&
    !isSearchingCompanies &&
    debouncedSearchInput === trimmedSearchInput
  const shouldFillResultsHeight = rankedLegalEntityResults.length > 8
  const isHeaderReady =
    !selectedLegalEntityHeader.isLoading &&
    Boolean(selectedLegalEntityHeader.item) &&
    String(selectedLegalEntityHeader.item?.id) === String(selectedLegalEntityId)
  const selectedName =
    selectedLegalEntityHeader.item?.tradeName &&
    String(selectedLegalEntityHeader.item.tradeName).substring(0, 3).toLowerCase() !== 'xxx'
      ? String(selectedLegalEntityHeader.item.tradeName)
      : String(selectedLegalEntityHeader.item?.corporateName || '')
  const selectedCityState = selectedLegalEntityHeader.item
    ? `${String(selectedLegalEntityHeader.item.city || '')} - ${String(selectedLegalEntityHeader.item.state || '')}`
    : ''

  return (
    <AppLayout dockActive={'analytical-library'} title={'Busca de Empresas'}>
      <PageContainer>
        <LeftSide>
          <Wrapper>
            <SearchInputWrapper>
              <input
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                // onKeyDown={handleSearchInputKeyDown}
                placeholder={'Busque por empresas...'}
              />
              {searchInput.length > 0 ? (
                <button
                  type={'button'}
                  className='input-action clear-action'
                  onClick={handleClearSearch}
                  aria-label={'Limpar busca'}
                >
                  ×
                </button>
              ) : null}
              <button
                type={'button'}
                className='input-action search-action'
                onClick={handleSearchAction}
                aria-label={'Selecionar primeira empresa'}
              >
                <SearchIcon width={16} height={16} fill={'#666666'} />
              </button>
            </SearchInputWrapper>

            <SearchMeta>
              {!canSearchFromInput
                ? 'Digite ao menos 3 caracteres para iniciar a busca'
                : isSearchingCompanies
                  ? 'Carregando empresas...'
                  : hasResults
                    ? `${rankedLegalEntityResults.length} ${
                        rankedLegalEntityResults.length === 1
                          ? 'empresa encontrada'
                          : 'empresas encontradas'
                      }`
                    : 'Nenhuma empresa encontrada.'}
            </SearchMeta>

            {shouldShowResults ? (
              <ResultsList $fillHeight={shouldFillResultsHeight} onScroll={handleResultListScroll}>
                {visibleResults.map((item: any) => (
                  <ResultItem
                    key={String(item.id)}
                    $isActive={selectedLegalEntityId === String(item.id)}
                    onClick={() => handleSelectLegalEntity(item)}
                  >
                    <TitleWrapper>
                      {String(item.tradeName || item.corporateName || '')}
                    </TitleWrapper>
                    <SubtitleWrapper>{String(item.subtitle || '')}</SubtitleWrapper>
                  </ResultItem>
                ))}
                {hasMoreItems ? (
                  <ListFooter>Role para baixo para carregar mais resultados.</ListFooter>
                ) : null}
              </ResultsList>
            ) : null}
          </Wrapper>
        </LeftSide>

        <Divider />

        <RightSide>
          {selectedLegalEntityId ? (
            <SelectedCompanyPanel>
              {isHeaderReady ? (
                <CompanySelected>
                  <CompanySelectedTitle>{selectedName || 'Empresa'}</CompanySelectedTitle>
                  <CompanySelectedMeta>
                    {selectedLegalEntityHeader.item?.cnpj ? (
                      <CompanySelectedTag>
                        {maskNumbers(String(selectedLegalEntityHeader.item.cnpj), masks.cnpj)}
                      </CompanySelectedTag>
                    ) : null}
                    {selectedCityState.trim() !== '-' ? (
                      <CompanySelectedTag>{selectedCityState}</CompanySelectedTag>
                    ) : null}
                  </CompanySelectedMeta>
                </CompanySelected>
              ) : null}
              <LegalEntityReadOneScope>
                <LegalEntityReadOne id={selectedLegalEntityId} hideHeader={true} />
              </LegalEntityReadOneScope>
            </SelectedCompanyPanel>
          ) : (
            <CenteredLogo>
              <Logo width={250} height={250} fill={colors.sebraeBlue} />
            </CenteredLogo>
          )}
        </RightSide>
      </PageContainer>
    </AppLayout>
  )
}
